/* Local arithmetic only. No network, storage, analytics or catalog mutation. */
(function (factory) {
  'use strict';
  const calculator = factory();
  if (typeof module === 'object' && module.exports) module.exports = calculator;
  if (typeof document !== 'undefined') calculator.mount(document);
})(function () {
  'use strict';
  const MAX = 1000000;
  const NUMBER = /^(?:\d+(?:\.\d*)?|\.\d+)$/;

  function readNumber(value, field, options) {
    const raw = value === null || value === undefined ? '' : String(value).trim();
    if (options.optional && raw === '') return null;
    if (!NUMBER.test(raw)) throw { field, message: 'Enter a positive number using a decimal point, without currency symbols or commas.' };
    const number = Number(raw);
    if (!Number.isFinite(number) || number < options.min || number > MAX) {
      throw { field, message: `Enter a number from ${options.min} to 1,000,000.` };
    }
    if (options.integer && !Number.isInteger(number)) throw { field, message: 'Enter a whole number of items.' };
    if (options.decimals !== undefined) {
      const fraction = raw.split('.')[1] || '';
      if (fraction.length > options.decimals) throw { field, message: `Use no more than ${options.decimals} decimal places.` };
    }
    return number;
  }

  function readPackage(input, number) {
    const price = readNumber(input.price, `price${number}`, { min: 0.01, decimals: 2 });
    const servings = readNumber(input.servings, `servings${number}`, { min: 0.001, decimals: 3 });
    const items = readNumber(input.items, `items${number}`, { min: 1, integer: true, optional: true });
    return { price, servings, items, perServing: price / servings, perItem: items === null ? null : price / items };
  }

  function compareUnit(a, b) {
    const equal = Math.abs(a - b) <= Math.max(a, b) * 1e-12;
    if (equal) return { lower: null, higher: null, difference: 0, percentLower: 0 };
    const higherValue = Math.max(a, b);
    const difference = Math.abs(a - b);
    const percentLower = Math.min(difference / higherValue * 100, 99.99999999999999);
    return { lower: a < b ? 'A' : 'B', higher: a > b ? 'A' : 'B', difference, percentLower };
  }

  function comparePackages(inputA, inputB) {
    const packages = [readPackage(inputA, 1), readPackage(inputB, 2)];
    return {
      packages,
      perServing: compareUnit(packages[0].perServing, packages[1].perServing),
      perItem: packages.every((item) => item.items !== null) ? compareUnit(packages[0].perItem, packages[1].perItem) : null
    };
  }

  function currencyFormat(currency, precision) {
    const code = /^[A-Z]{3}$/.test(currency || '') ? currency : 'USD';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: code, currencyDisplay: 'code', minimumFractionDigits: 2, maximumFractionDigits: precision });
  }

  function formatUnit(value, formatter) {
    return value > 0 && value < 0.000001 ? `less than ${formatter.format(0.000001)}` : formatter.format(value);
  }

  function describeDifference(comparison, unit, money, percent) {
    if (!comparison.lower) return `Both packages have the same cost per ${unit} for the numbers entered.`;
    const percentage = comparison.percentLower < 0.01 ? 'less than 0.01%' : comparison.percentLower > 99.99 ? 'more than 99.99%' : `${percent.format(comparison.percentLower)}%`;
    return `Package ${comparison.lower} has a lower cost per ${unit}, with a difference of ${formatUnit(comparison.difference, money)}: ${percentage} lower than package ${comparison.higher}, using package ${comparison.higher}’s unit price as the reference.`;
  }

  function mount(scope) {
    scope.querySelectorAll('[data-wcn-value]').forEach((root) => {
      if (root.dataset.valueMounted === 'true') return;
      root.dataset.valueMounted = 'true';
      const form = root.querySelector('[data-value-form]');
      const status = root.querySelector('[data-value-status]');
      const results = root.querySelector('[data-value-results]');
      const summary = root.querySelector('[data-value-summary]');
      const itemSummary = root.querySelector('[data-value-item-summary]');
      const fields = Array.from(form.querySelectorAll('[data-value-field]'));
      const cash = currencyFormat(root.dataset.currency, 2);
      const unitCash = currencyFormat(root.dataset.currency, 6);
      const numbers = new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 });
      const percentages = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
      form.hidden = false;

      function clearErrors() {
        fields.forEach((field) => field.removeAttribute('aria-invalid'));
        root.querySelectorAll('[data-value-error]').forEach((error) => { error.hidden = true; error.textContent = ''; });
      }
      function read(index) {
        return { price: form.elements[`price${index}`].value, servings: form.elements[`servings${index}`].value, items: form.elements[`items${index}`].value };
      }
      function setOutput(key, value) {
        root.querySelector(`[data-value-output="${key}"]`).textContent = value;
      }
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        clearErrors();
        let result;
        try {
          result = comparePackages(read(1), read(2));
        } catch (error) {
          results.hidden = true;
          const field = form.elements[error.field];
          if (!field) { status.textContent = 'The comparison could not be calculated. Check the numbers and try again.'; return; }
          const message = root.querySelector(`[data-value-error="${error.field}"]`);
          field.setAttribute('aria-invalid', 'true');
          message.textContent = error.message;
          message.hidden = false;
          status.textContent = 'Check the highlighted field, then compare again.';
          field.focus();
          return;
        }
        result.packages.forEach((item, index) => {
          const number = index + 1;
          setOutput(`price${number}`, cash.format(item.price));
          setOutput(`servings${number}`, numbers.format(item.servings));
          setOutput(`perServing${number}`, formatUnit(item.perServing, unitCash));
          root.querySelectorAll(`[data-value-item-row="${number}"]`).forEach((row) => { row.hidden = item.items === null; });
          setOutput(`items${number}`, item.items === null ? '' : numbers.format(item.items));
          setOutput(`perItem${number}`, item.perItem === null ? '' : formatUnit(item.perItem, unitCash));
        });
        summary.textContent = describeDifference(result.perServing, 'labeled serving', unitCash, percentages);
        itemSummary.hidden = result.perItem === null;
        itemSummary.textContent = result.perItem ? describeDifference(result.perItem, 'item', unitCash, percentages) + ' Item sizes and contents still need to be comparable.' : '';
        results.hidden = false;
        status.textContent = 'Comparison updated. ' + summary.textContent;
      });
      form.addEventListener('input', () => {
        if (!results.hidden) {
          results.hidden = true;
          status.textContent = 'Inputs changed. Select Compare package costs to update the result.';
        }
      });
      form.addEventListener('reset', () => {
        clearErrors();
        results.hidden = true;
        summary.textContent = '';
        itemSummary.textContent = '';
        status.textContent = 'Calculator cleared.';
      });
    });
  }

  return { comparePackages, compareUnit, currencyFormat, formatUnit, describeDifference, mount };
});
